import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { pprwAPI, taxonomyAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Plus, Loader2, Layers, X, Calendar, Users, Wallet, MonitorPlay, LayoutTemplate } from 'lucide-react';
import { WorkshopFormDialog } from '../components/pprw/WorkshopFormDialog';
import { AddSessionsDialog } from '../components/pprw/AddSessionsDialog';

const isBlocking = (s) => ['not started', 'delayed'].includes((s.content_readiness_status || '').toLowerCase()) || ['not started', 'delayed'].includes((s.lab_readiness_status || '').toLowerCase());

const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };

const statusBadge = (status) => {
  const cls = {
    Scheduled: 'bg-blue-100 text-blue-700', Upcoming: 'bg-purple-100 text-purple-700',
    'In Progress': 'bg-amber-100 text-amber-700', Completed: 'bg-emerald-100 text-emerald-700',
    'On Hold': 'bg-orange-100 text-orange-700', Cancelled: 'bg-red-100 text-red-700',
  }[status] || 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status || '—'}</span>;
};

const tzBadge = (tz) => {
  const cls = { IST: 'bg-blue-100 text-blue-700', BST: 'bg-emerald-100 text-emerald-700', GMT: 'bg-amber-100 text-amber-700', PST: 'bg-purple-100 text-purple-700', PDT: 'bg-pink-100 text-pink-700' }[tz] || 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{tz || '—'}</span>;
};

const InfoItem = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-2">
    {Icon && <Icon className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />}
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium truncate">{value || '—'}</p>
    </div>
  </div>
);

// Side-by-side time-zone schedule: rows = dates, columns = time zones.
function TimezoneGrid({ sessions }) {
  const withDate = (sessions || []).filter(s => s.start_date);
  if (withDate.length === 0) return null;
  const tzs = Array.from(new Set(withDate.map(s => s.time_zone || '—'))).sort();
  const dates = Array.from(new Set(withDate.map(s => s.start_date))).sort();
  const cell = (date, tz) => withDate.filter(s => s.start_date === date && (s.time_zone || '—') === tz);
  return (
    <div className="rounded-xl border border-border overflow-auto" data-testid="workshop-timezone-grid">
      <table className="w-full text-sm">
        <thead className="bg-muted">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Date</th>
            {tzs.map(tz => <th key={tz} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tz}</th>)}
          </tr>
        </thead>
        <tbody>
          {dates.map(date => (
            <tr key={date} className="border-t border-border">
              <td className="px-3 py-2 font-medium whitespace-nowrap align-top">{fmtDate(date)}</td>
              {tzs.map(tz => (
                <td key={tz} className="px-3 py-2 align-top">
                  {cell(date, tz).map(s => (
                    <Link key={s.id} to={`/pprw/${s.id}`} className="block mb-1 last:mb-0 rounded px-2 py-1 bg-lime/10 hover:bg-lime/20 transition-colors text-xs" data-testid={`tz-grid-session-${s.id}`}>
                      <span className="font-medium">{(s.start_time || s.session_time || '—')}{s.end_time ? `–${s.end_time}` : ''}</span>
                      <span className="block text-muted-foreground truncate">{[s.trainer_1, s.trainer_2].filter(Boolean).join(', ') || 'No trainer'}</span>
                    </Link>
                  ))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PPRWWorkshopDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [searchParams] = useSearchParams();
  const [blockingOnly, setBlockingOnly] = useState(searchParams.get('filter') === 'blocking');

  const load = useCallback(async () => {
    setLoading(true);
    try { setWorkshop(await pprwAPI.workshops.get(id)); }
    catch (_e) { toast.error('Failed to load workshop'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { taxonomyAPI.getAll().then(setCategories).catch(() => {}); }, []);

  const handleRemove = async (sessionId) => {
    if (!window.confirm('Remove this session from the workshop? It will become a standalone session.')) return;
    setRemoving(sessionId);
    try { await pprwAPI.workshops.removeSessions(id, [sessionId]); toast.success('Session removed'); load(); }
    catch { toast.error('Remove failed'); }
    finally { setRemoving(null); }
  };

  const handleSaveTemplate = async () => {
    const name = window.prompt('Template name:', workshop?.title || '');
    if (!name) return;
    try { await pprwAPI.workshops.saveAsTemplate(id, name); toast.success('Saved as template — reuse it from “From Template”'); }
    catch { toast.error('Failed to save template'); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!workshop) return <div className="p-6 text-muted-foreground">Workshop not found.</div>;

  const sessions = workshop.sessions || [];
  const shownSessions = blockingOnly ? sessions.filter(isBlocking) : sessions;

  return (
    <div className="p-6 space-y-6" data-testid="workshop-detail-page">
      <button onClick={() => navigate('/pprw')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="workshop-back-btn">
        <ArrowLeft className="w-4 h-4" />Back to 1:Many PPRW
      </button>

      {/* Common info header */}
      <div className="rounded-xl border border-border bg-background p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Layers className="w-5 h-5 text-lime flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl font-bold truncate" data-testid="workshop-title">{workshop.title}</h1>
              <p className="text-sm text-muted-foreground">{sessions.length} session{sessions.length !== 1 ? 's' : ''} · common attributes shown below</p>
              {workshop.readiness && (workshop.readiness.content || workshop.readiness.lab) && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="workshop-readiness-header">
                  {workshop.readiness.at_risk ? (
                    <button onClick={() => setBlockingOnly(v => !v)} title="Toggle blocking sessions"
                      className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${blockingOnly ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'}`}
                      data-testid="workshop-readiness-pill">At risk</button>
                  ) : (
                    <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">On track</span>
                  )}
                  <span className="text-[11px] text-muted-foreground">Content: {workshop.readiness.content || '—'} · Lab: {workshop.readiness.lab || '—'}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" className="gap-2" onClick={handleSaveTemplate} data-testid="workshop-save-template-btn">
              <LayoutTemplate className="w-3.5 h-3.5" />Save as Template
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowEdit(true)} data-testid="workshop-edit-header-btn">
              <Pencil className="w-3.5 h-3.5" />Edit Common Info
            </Button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoItem label="Solution Area" value={workshop.solution_area} />
          <InfoItem label="MS Owner" value={workshop.ms_owner} icon={Users} />
          <InfoItem label="PMO" value={workshop.pmo} />
          <InfoItem label="Delivery Platform" value={workshop.delivery_platform} icon={MonitorPlay} />
          <InfoItem label="Delivery Type" value={workshop.delivery_type} />
          <InfoItem label="Quarter" value={workshop.quarter} />
          <InfoItem label="Start Date" value={fmtDate(workshop.start_date)} icon={Calendar} />
          <InfoItem label="End Date" value={fmtDate(workshop.end_date)} icon={Calendar} />
          <InfoItem label="PO Number" value={workshop.po_number} icon={Wallet} />
          <InfoItem label="Series / Journey" value={workshop.series_journey_title} />
          <InfoItem label="Content Owner" value={workshop.content_owner} />
          <InfoItem label="Lab Owner" value={workshop.lab_owner} />
        </div>
        {workshop.description && <p className="mt-4 text-sm text-muted-foreground border-t border-border/60 pt-3">{workshop.description}</p>}
      </div>

      {/* Blocking filter banner */}
      {blockingOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm" data-testid="blocking-filter-banner">
          <span className="text-red-700">Showing {shownSessions.length} session{shownSessions.length !== 1 ? 's' : ''} with blocking content/lab readiness</span>
          <button onClick={() => setBlockingOnly(false)} className="ml-auto text-xs text-red-700 underline" data-testid="clear-blocking-filter">Clear filter</button>
        </div>
      )}

      {/* Time-zone schedule */}
      {shownSessions.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Time-zone Schedule</h2>
          <TimezoneGrid sessions={shownSessions} />
        </div>
      )}

      {/* Sessions inside */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Sessions in this Workshop</h2>
          <Button size="sm" className="gap-2" onClick={() => setShowAdd(true)} data-testid="workshop-add-sessions-btn">
            <Plus className="w-4 h-4" />Add Sessions
          </Button>
        </div>
        <div className="rounded-xl border border-border overflow-auto" data-testid="workshop-sessions-table">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                {['Course', 'Date', 'TZ', 'Start–End', 'Trainer(s)', 'Status', ''].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownSessions.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground text-sm">{blockingOnly ? 'No blocking sessions — all content/lab readiness is on track.' : 'No sessions yet. Click “Add Sessions” to attach standalone sessions.'}</td></tr>
              )}
              {shownSessions.map(s => (
                <tr key={s.id} className="border-t border-border hover:bg-muted/30 group" data-testid={`workshop-session-row-${s.id}`}>
                  <td className="px-3 py-2.5 font-medium max-w-[220px] truncate">
                    <Link to={`/pprw/${s.id}`} className="hover:underline hover:text-lime-700">{s.course_title}</Link>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs">{fmtDate(s.start_date)}</td>
                  <td className="px-3 py-2.5">{tzBadge(s.time_zone)}</td>
                  <td className="px-3 py-2.5 text-xs whitespace-nowrap">{(s.start_time || s.session_time || '—')}{s.end_time ? `–${s.end_time}` : ''}</td>
                  <td className="px-3 py-2.5 text-xs max-w-[160px] truncate">{[s.trainer_1, s.trainer_2].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-3 py-2.5">{statusBadge(s.delivery_status)}</td>
                  <td className="px-3 py-2.5">
                    <button className="p-1.5 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove from workshop"
                      onClick={() => handleRemove(s.id)} disabled={removing === s.id} data-testid={`workshop-remove-session-${s.id}`}>
                      {removing === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-red-400" /> : <X className="w-3.5 h-3.5 text-red-400" />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showEdit && <WorkshopFormDialog open={showEdit} onClose={() => setShowEdit(false)} onSaved={load} existing={workshop} categories={categories} />}
      {showAdd && <AddSessionsDialog open={showAdd} onClose={() => setShowAdd(false)} workshopId={id} onAdded={load} />}
    </div>
  );
}

export default PPRWWorkshopDetailPage;
